// 清理 URL 中的 csrfToken 参数（passport 登出后重定向可能带上）
if (window.location.search.includes('csrfToken')) {
    const url = new URL(window.location.href);
    url.searchParams.delete('csrfToken');
    window.history.replaceState({}, '', url.pathname + url.hash);
}

// 早期登录状态检查：未登录时跳转到 /vault/guest/
// 通过检查 localStorage 中的 token 来判断登录状态
const earlyLoginCheck = () => {
    const pathname = window.location.pathname;
    const isGuestPath = pathname.includes('/vault/guest/');

    // 检查是否有 token（简单判断登录状态）
    const hasToken = (() => {
        const possibleKeys = ['patsnap-authorizations', 'access_token', 'authToken', 'token'];
        for (const key of possibleKeys) {
            const value = localStorage.getItem(key);
            if (value && value.length > 10) return true;
        }
        return false;
    })();

    if (!hasToken && !isGuestPath && pathname.match(/^\/vault\/?$/)) {
        // 未登录且在 /vault/ 根路径，立即跳转到 guest
        window.location.href = `${window.location.origin}/vault/guest/${window.location.search}${window.location.hash}`;
        return true; // 表示已跳转
    }
    return false;
};

// 执行早期检查，如果已跳转则停止执行后续代码
if (earlyLoginCheck()) {
    throw new Error('Redirecting to guest page');
}

import 'reflect-metadata';
import './fix';
import { createApp } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';
import './styles/main.scss';
import '@patsnap-biz/design-tokens/scss/_variables-RD.css';

// 显式导入 nav-bar 的 SVG 图标，确保它们被 svgSpritePlugin 处理
import '@eureka/shared/nav-bar/src/assets/logo.svg';
import '@eureka/shared/nav-bar/src/assets/mini-logo.svg';
import App from './App.vue';
import Layout from './pages/Layout.vue';
import { container } from './inversify.config';
import { setContainer, injectService } from '@patsnap/tahiti-core/ioc/inject';
import { startup } from '@eureka/startup';
import { i18n } from '@/locales';
import { HeaderServiceProxy } from './services/header-service-proxy';
import { AnonymousError } from '@eureka/services/src/exception';
import { injectFingerprintInterceptor } from '@eureka/services/src/apis/interceptors/fingerprint.interceptor';
import { SafeHtmlPlugin } from '@eureka/shared/safe-html-plugin';
import { loginSDK } from '@eureka/login-sdk';
import { accountDeferred } from '@eureka/utils/account';

// Set up the InversifyJS container
setContainer(container);

// 初始化 loginSDK 的 i18n
loginSDK.setI18n(i18n);
injectFingerprintInterceptor();

const headerServiceInstance = injectService<HeaderServiceProxy>(HeaderServiceProxy).headerService;

// 配置数组,可以根据需要进行修改（这些页面不需要登录即可访问）
const detailPageRegExps = [
    /^\/$/,           // 根路径
    /^\/home\/?$/,    // Home 页面
    /^\/home\/solutions\/?$/,  // Solutions 页面
];

// 判断是否为详情页面（不需要登录的页面）
const isDetailPage = () => {
    const fullPath = window.location.pathname;
    // 移除 /vault/、/vault/x.x.x/ 或 /vault/guest/ 前缀，获取相对路径
    const relativePath = fullPath.replace(/^\/vault(\/[\d.]+|\/guest)?/, '') || '/';
    return detailPageRegExps.some(pathReg => pathReg.test(relativePath));
};

startup({
    headerService: headerServiceInstance,
    projectName: 'eureka',
    preset: {
        name: 'eureka',
        preRedirectLogin() {
            const isSkipPage = isDetailPage() && !headerServiceInstance?.userCenterService?.logged;
            if (!isSkipPage) {
                loginSDK.open().then(() => {
                    // 登录成功后，如果在 guest 路径则跳转到 /vault/，否则刷新页面
                    if (window.location.pathname.includes('/vault/guest/')) {
                        window.location.href = `${window.location.origin}/vault/`;
                    } else {
                        window.location.reload();
                    }
                }).catch(() => {});
            }
            throw new AnonymousError();
        },
    },
    pageInit() {
        return Promise.resolve().then(() => {
            window.hidePageLoading?.();
        });
    },
});

// 判断当前是否在 guest 路径
const isGuestPath = () => {
    return window.location.pathname.includes('/vault/guest/');
};

// 根据登录状态自动跳转
// - 已登录 + 在 /vault/guest/ → 跳转到 /vault/
// - 未登录 + 在 /vault/（非 guest）→ 跳转到 /vault/guest/
const handleLoginRedirect = (isLoggedIn: boolean) => {
    const hash = window.location.hash;
    const search = window.location.search;

    if (isLoggedIn && isGuestPath()) {
        // 已登录但在 guest 路径，跳转到 /vault/（不保留参数）
        window.location.href = `${window.location.origin}/vault/`;
    } else if (!isLoggedIn && !isGuestPath()) {
        // 未登录且不在 guest 路径，跳转到 guest 路径
        // 支持 /vault/ 和 /vault/x.x.x/ 格式
        window.location.href = `${window.location.origin}/vault/guest/${search}${hash}`;
    }
};

// 初始化帮助中心 & 登录状态跳转
accountDeferred.promise.then(() => {
    const isLoggedIn = !!headerServiceInstance?.userCenterService?.logged;

    // 处理登录状态跳转
    handleLoginRedirect(isLoggedIn);

    // 初始化帮助中心
    import('@eureka/startup/src/help-center').then(({ getHelpCenter }) => {
        getHelpCenter(i18n.global, headerServiceInstance);
    });
});

// 获取 vault 的 base path（支持版本化部署和 guest 路径）
const getVaultBasePath = () => {
    const pathname = window.location.pathname
    // 支持 /vault/x.x.x/、/vault/guest/ 和 /vault/
    const match = pathname.match(/^(\/vault\/[\d.]+\/|\/vault\/guest\/|\/vault\/)/)
    return match ? match[1] : '/vault/'
}

// 覆盖 NavBar 的退出登录处理，使其跳转到 /vault/ 而不是 /home
// 需要等待 headerService 初始化完成后再覆盖
headerServiceInstance?.deferred?.promise?.then(() => {
    const userCenterService = headerServiceInstance?.userCenterService;
    if (userCenterService?.beforeLogoutHandlers) {
        // 清空现有的 handlers
        userCenterService.beforeLogoutHandlers.clear();
        // 添加我们自己的 handler
        userCenterService.beforeLogoutHandlers.add(async function() {
            userCenterService.logged = false;
            userCenterService.setUserInfo?.();
            localStorage.removeItem('patsnap-authorizations');
            // 获取 passport logout URL，退出后跳转到 guest 页面
            const passportHost = import.meta.env.VITE_PASSPORT_URI || 'https://passport.zhihuiya.com';
            const redirectUri = `${location.origin}/vault/guest/`;
            const logoutUrl = `${passportHost}/logout?from=eureka&redirect_uri=${encodeURIComponent(redirectUri)}`;
            location.href = logoutUrl;
            throw new Error('Logout in Vault');
        });
    }
}).catch(() => {});

// 动态获取路由 base 路径（支持版本化部署和 guest 路径）
const getRouterBase = () => {
    const pathname = window.location.pathname;
    // 匹配 /vault/x.x.x/、/vault/guest/ 或 /vault/
    const match = pathname.match(/^(\/vault\/[\d.]+\/|\/vault\/guest\/|\/vault\/)/);
    return match ? match[1] : '/vault/';
};

// 创建路由（嵌套路由结构，Layout 包含公共 NavBar）
const router = createRouter({
    history: createWebHashHistory(),
    routes: [
        // Home 页面（独立页面，无 Layout）
        {
            path: '/home',
            name: 'Home',
            component: () => import('./views/Home.vue'),
        },
        {
            path: '/home/solutions',
            name: 'Solutions',
            component: () => import('./views/Solutions.vue'),
        },
        // 主应用路由（带 Layout）
        // 使用查询参数 ?kb=xxx 进入知识库，避免 SPA 路由刷新 404 问题
        {
            path: '/',
            component: Layout,
            children: [
                {
                    path: '',
                    name: 'Main',
                    component: () => import('./views/MainView.vue'),
                },
            ],
        },
    ],
});

(async () => {
    const app = createApp(App);
    app.use(router).use(i18n).use(ElementPlus).use(SafeHtmlPlugin).mount('#app');

    // 暴露 router 到 window 方便调试
    (window as any).router = router;
})();
